/**
 * Floor loyalty attach — F-FE-N61.
 *
 * The Bhookly Server APK's answer to "the guest wants their card punched".
 * Before this, the only surface was /scan on a separate phone, which made the
 * server pick the right bill out of a flat list of open orders. Order numbers
 * mean nothing to someone working a floor — they think "table 7" — so the list
 * was both an extra navigation step and the one real chance to attach a guest
 * to the wrong bill.
 *
 * Here the table is already chosen: the rail opens this with the order it is
 * showing, so there is nothing to pick and nothing to get wrong. The table
 * number stays pinned above the viewfinder for the same reason.
 *
 * Attach-only, exactly like /scan: the punch is awarded by the payment path
 * when the till settles the bill. Nothing here grants a stamp, which is why
 * `server` is allowed to run it at all (see counterFloor in routes.go).
 */
import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, ScanLine, UserCheck } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { toastHelpers } from '@/lib/toast-helpers'
import { cameraScanSupported } from '@/lib/scannerCamera'
import {
  CameraCardScanner,
  ScanError,
  describeScanError,
} from '@/components/counter/loyaltyCardScanner'
import type { ResolvedLoyaltyCard } from '@/types'
import { invalidateTables } from '@/lib/tablesCache'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  tableNumber: string | number
  orderId: string
  orderNumber: string
  /** Guest already on the bill, so a re-scan can say what it is replacing. */
  currentGuestName?: string | null
}

export function ServerScanLoyaltyDialog({
  open,
  onOpenChange,
  tableNumber,
  orderId,
  orderNumber,
  currentGuestName,
}: Props) {
  const queryClient = useQueryClient()
  const [card, setCard] = useState<ResolvedLoyaltyCard | null>(null)
  const [code, setCode] = useState('')
  // Camera runs by default — this dialog exists to point a lens at a card.
  // The typed path stays for USB wedges, dead cameras, and the tablet whose
  // owner denied the OS camera permission (same dual input as the till).
  const [camera, setCamera] = useState(false)

  // Reset every time it opens: a stale resolved card from the last table is
  // exactly the wrong-bill mistake this component exists to prevent.
  useEffect(() => {
    if (open) {
      setCard(null)
      setCode('')
      setCamera(cameraScanSupported())
    }
  }, [open])

  // Venue-level toggle. Same probe /scan uses, so flipping it off in admin
  // settings hides this everywhere at once rather than leaving the floor with
  // a button that 400s.
  const { data: scanEnabled } = useQuery({
    queryKey: ['loyalty', 'phone-scan-enabled'],
    queryFn: () => apiClient.getPhoneScanEnabled().then((r) => r.data?.enabled ?? false),
    enabled: open,
    staleTime: 60_000,
  })

  const resolveMutation = useMutation({
    mutationFn: async (rawToken: string) => {
      const token = rawToken.trim()
      if (!token) throw new Error('empty')
      const res = await apiClient.resolveLoyaltyCard(token)
      const found = res.data
      if (!found || !found.enabled) {
        throw new ScanError('loyalty_off', 'Loyalty is off — enable it in admin settings.')
      }
      if (!found.customer_phone) {
        throw new ScanError('no_phone', 'This card has no phone on file to attach.')
      }
      return found
    },
    onSuccess: (found) => {
      setCode('')
      setCard(found)
    },
    onError: (err) => {
      const { title, description } = describeScanError(err)
      toastHelpers.error(title, description)
      setCode('')
      // Re-arm — a decode closes the camera, and the usual failure is a
      // mis-read rather than a wrong card.
      if (cameraScanSupported()) setCamera(true)
    },
  })

  const attachMutation = useMutation({
    mutationFn: async () => {
      if (!card) throw new Error('no card')
      const res = await apiClient.updateCounterOrderGuest(orderId, {
        customer_phone: card.customer_phone as string,
        customer_name: card.customer_name || undefined,
      })
      if (!res.success || !res.data) {
        throw new Error(res.message || 'Could not attach the customer')
      }
      return res.data
    },
    onSuccess: () => {
      toastHelpers.success(
        'Guest attached',
        `${card?.customer_name || card?.customer_phone} is on Table ${tableNumber}.`,
      )
      // The rail reads the order query; the floor tiles read tables.
      queryClient.invalidateQueries({ queryKey: ['order', orderId] })
      invalidateTables(queryClient)
      onOpenChange(false)
    },
    onError: (err) => {
      const { title, description } = describeScanError(err)
      toastHelpers.error(title, description)
    },
  })

  function submitTyped() {
    if (resolveMutation.isPending || !code.trim()) return
    resolveMutation.mutate(code)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanLine className="h-4 w-4" />
            Table {tableNumber}
          </DialogTitle>
          <DialogDescription>
            <span className="font-mono">#{orderNumber}</span>
            {currentGuestName ? ` · currently ${currentGuestName}` : ''}
          </DialogDescription>
        </DialogHeader>

        {scanEnabled === false ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Loyalty scanning is turned off for this venue. An admin can enable it
            under Marketing → Loyalty.
          </p>
        ) : card ? (
          // ── Confirm ────────────────────────────────────────────────────
          // Always a deliberate second tap. The scan is the easy half; putting
          // the wrong person on a bill is the expensive half.
          <div className="space-y-4">
            <div className="rounded-lg border border-border/80 p-3">
              <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Guest
              </div>
              <div className="mt-1 text-base font-semibold">
                {card.customer_name || 'Unnamed guest'}
              </div>
              <div className="text-sm text-muted-foreground">{card.customer_phone}</div>
            </div>

            {currentGuestName && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                This replaces <span className="font-medium">{currentGuestName}</span> on
                the bill.
              </p>
            )}

            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setCard(null)
                  if (cameraScanSupported()) setCamera(true)
                }}
                disabled={attachMutation.isPending}
              >
                Scan again
              </Button>
              <Button
                onClick={() => attachMutation.mutate()}
                disabled={attachMutation.isPending}
              >
                {attachMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <UserCheck className="mr-2 h-4 w-4" />
                )}
                Attach
              </Button>
            </div>
          </div>
        ) : (
          // ── Scan ───────────────────────────────────────────────────────
          <div className="space-y-3">
            {camera && (
              <CameraCardScanner
                onDecode={(token) => {
                  setCamera(false)
                  resolveMutation.mutate(token)
                }}
                onClose={() => setCamera(false)}
                busy={resolveMutation.isPending}
              />
            )}

            {!camera && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setCamera(true)}
                disabled={!cameraScanSupported() || resolveMutation.isPending}
              >
                <ScanLine className="mr-2 h-4 w-4" />
                {cameraScanSupported() ? 'Open camera' : 'No camera on this device'}
              </Button>
            )}

            <div className="flex items-center gap-1.5">
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    submitTyped()
                  }
                }}
                placeholder="or type the card code"
                autoComplete="off"
              />
              <Button
                onClick={submitTyped}
                disabled={resolveMutation.isPending || !code.trim()}
              >
                {resolveMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Find'
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
